import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import type { Scene } from '@babylonjs/core/scene';
import { FRONTAGES, PALETTE, segments } from '@fisura/shared';

/**
 * Building accents along the avenue frontages: per-floor balcony ledges +
 * railings and a roofline cornice (driven by the shared FRONTAGES data), plus
 * shop awnings over the standable kiosks/shelters. The windowed facade itself
 * is now generated as carvable `facade-*` boxes in the map (see facade.ts +
 * world.ts). Decorative only — no colliders.
 */

// Shop awnings — strictly palette: warm accent alternating with cool greys, so
// storefronts read as distinct without ever drifting to green/yellow.
const AWNING_COLORS: [number, number, number][] = [
  PALETTE.accent,
  PALETTE.slate,
  PALETTE.accent,
  PALETTE.mid,
];

// The solid sidewalk kiosks/shelters (map.ts) are the few standable "shops" —
// give each an awning so it reads as one. [x, z, faceToAvenue, width].
const SHOP_AWNINGS: [number, number, number, number][] = [
  [-20, -11.5, 1, 3], // south news kiosks (face +z, the avenue)
  [2, -11.5, 1, 3],
  [40, -11.5, 1, 3],
  [-34, 10.75, -1, 6], // north bus shelters (face -z)
  [28, 10.75, -1, 6],
];

export function buildFacadeDecor(scene: Scene): void {
  const groups = new Map<StandardMaterial, Mesh[]>();
  const cache = new Map<string, StandardMaterial>();
  const mat = (key: string, c: [number, number, number]): StandardMaterial => {
    let m = cache.get(key);
    if (!m) {
      m = new StandardMaterial(`decor-${key}`, scene);
      m.diffuseColor = new Color3(c[0], c[1], c[2]);
      m.specularColor = new Color3(0.04, 0.04, 0.04);
      cache.set(key, m);
    }
    return m;
  };
  const add = (m: StandardMaterial, cx: number, cy: number, cz: number, sx: number, sy: number, sz: number): void => {
    const mesh = MeshBuilder.CreateBox('decor', { width: sx, height: sy, depth: sz }, scene);
    mesh.position = new Vector3(cx, cy, cz);
    mesh.material = m;
    const g = groups.get(m);
    if (g) g.push(mesh);
    else groups.set(m, [mesh]);
  };

  const ledgeM = mat('ledge', PALETTE.light);
  const railM = mat('rail', PALETTE.ink);
  const corniceM = mat('cornice', PALETTE.mid);

  for (const f of FRONTAGES) {
    for (const sp of f.spans) {
      const top = sp.top;
      for (const [a, b] of segments(sp.x0, sp.x1, f.doors)) {
        const len = b - a;
        const cx = (a + b) / 2;
        // Balcony ledges + railings per floor (only up to this segment's top).
        for (let y = 3; y < top - 0.5; y += 3) {
          add(ledgeM, cx, y - 0.11, f.z + f.face * 0.55, len, 0.22, 0.7);
          add(railM, cx, y + 0.28, f.z + f.face * 0.9, len, 0.5, 0.1);
        }
        // Roofline cornice.
        add(corniceM, cx, top - 0.2, f.z + f.face * 0.35, len + 0.5, 0.5, 0.9);
      }
    }
  }

  // Awnings over the existing solid kiosks/shelters (the standable shops).
  SHOP_AWNINGS.forEach(([x, z, face, w], i) => {
    const aw = mat(`awn-${i % AWNING_COLORS.length}`, AWNING_COLORS[i % AWNING_COLORS.length]!);
    add(aw, x, 2.55, z + face * 1.5, w, 0.2, 1.5); // sloped-look slab over the front
    add(railM, x, 2.2, z + face * 2.2, w, 0.7, 0.06); // valance lip at the awning edge
  });

  for (const [m, meshes] of groups) {
    const merged = meshes.length === 1 ? meshes[0]! : Mesh.MergeMeshes(meshes, true, true);
    if (merged) {
      merged.material = m;
      merged.isPickable = false;
      merged.freezeWorldMatrix();
    }
  }
}
